import { useEffect, useState, useRef, useCallback, useMemo, memo, forwardRef, useImperativeHandle } from 'react'
import { X, ArrowLeft, Paperclip, Send, Loader2, Plus, MessageSquare, Play, Clock, MonitorUp, Reply, Info } from 'lucide-react'
import { useWidgetChannel, TypingData, MessageUpdatedData } from '@/hooks/useWidgetChannel'
import { useEndUserChannel, NewMessageNotification, MessageDeletedNotification } from '@/hooks/useEndUserChannel'
import MessageBody from '@/components/MessageBody'
import QuotedMessage from '@/components/QuotedMessage'
import { stripHtml } from '@/lib/sanitizeHtml'

// Types
interface Agent {
  id: number
  name: string
  avatar_url: string | null
  online: boolean
}

interface EndUser {
  id: number
  name: string
}

interface Attachment {
  id: number
  signed_id: string
  filename: string
  content_type: string
  byte_size: number
  url: string
  is_video?: boolean
  is_image?: boolean
}

interface Message {
  id: number
  body: string
  author_type: 'Agent' | 'EndUser' | null
  author: Agent | EndUser | { id: null; name: string }
  created_at: string
  deleted_at: string | null
  edited_at?: string | null
  attachments: Attachment[]
  system_message_type?: 'away_notice' | 'email_capture' | 'rating_request' | null
  metadata?: {
    email_captured?: boolean
    email?: string
    rated?: boolean
    rating?: number
    agent_id?: number
    agent_name?: string
    has_comment?: boolean
    outbound_post?: boolean
    outbound_message_id?: number
    post_format?: 'small' | 'large'
    title?: string
    button_text?: string
    button_url?: string
  } | null
  replied_to?: {
    id: number
    body: string | null
    author_type: 'Agent' | 'EndUser' | null
    author_name: string
    attachments_count: number
    deleted: boolean
    image_url?: string | null
  } | null
}

interface Conversation {
  id: number
  status: string
  closed_at?: string | null
  assigned_agent: Agent | null
  last_message: string | null
  last_message_author_name: string | null
  last_message_has_attachments: boolean
  last_message_at: string
  unread_count: number
  reply_deadline_at?: string | null
  reply_allowed?: boolean
}

interface WidgetConfig {
  appId: string
  sessionToken: string
  apiUrl: string
  brandColor: string
  welcomeText: string
  embedded?: boolean
  currentPageUrl?: string
  userData?: {
    userId?: string
    email?: string
    name?: string
  }
}

interface Availability {
  available: boolean
  message: string | null
  next_open_at: string | null
  next_open_time: string | null
  organization_name: string | null
  exception_name: string | null
  exception_message: string | null
  in_exception: boolean
}

type ViewType = 'detail' | 'list'

const MAX_COMPOSER_ROWS = 8

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`
  return date.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })
}

function formatDateSeparator(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Hoje'
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Ontem'
  }
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })
}

function shouldShowDateSeparator(
  currentDate: string,
  previousDate: string | null,
  isFirstMessage: boolean,
  hasMultipleDays: boolean
): boolean {
  if (isFirstMessage) return hasMultipleDays
  if (!previousDate) return false
  const current = new Date(currentDate)
  const previous = new Date(previousDate)
  return current.toDateString() !== previous.toDateString()
}

function conversationHasMultipleDays(messages: { created_at: string }[]): boolean {
  if (messages.length < 2) return false
  const firstDate = new Date(messages[0].created_at).toDateString()
  return messages.some(m => new Date(m.created_at).toDateString() !== firstDate)
}

// Isolated typing indicator component — its state changes never re-render the parent
interface TypingIndicatorHandle {
  onTypingReceived: (data: TypingData) => void
  onAgentMessageReceived: (agentId: number) => void
}

const TypingIndicator = memo(forwardRef<TypingIndicatorHandle>(function TypingIndicator(_, ref) {
  const [typingUsers, setTypingUsers] = useState<TypingData[]>([])
  const typingTimeoutRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  const lastAgentMessageTimeRef = useRef<Record<number, number>>({})

  useImperativeHandle(ref, () => ({
    onTypingReceived(typingData: TypingData) {
      if (typingData.user_type !== 'Agent') return

      if (typingData.is_typing) {
        const lastMessageTime = lastAgentMessageTimeRef.current[typingData.user_id]
        if (lastMessageTime && Date.now() - lastMessageTime < 1000) return

        setTypingUsers(prev => {
          if (prev.some(t => t.user_id === typingData.user_id)) return prev
          return [...prev, typingData]
        })

        if (typingTimeoutRef.current[typingData.user_id]) {
          clearTimeout(typingTimeoutRef.current[typingData.user_id])
        }
        typingTimeoutRef.current[typingData.user_id] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(t => t.user_id !== typingData.user_id))
        }, 3000)
      } else {
        setTypingUsers(prev => prev.filter(t => t.user_id !== typingData.user_id))
      }
    },
    onAgentMessageReceived(agentId: number) {
      if (agentId) lastAgentMessageTimeRef.current[agentId] = Date.now()
      setTypingUsers([])
      for (const timeout of Object.values(typingTimeoutRef.current)) {
        clearTimeout(timeout)
      }
      typingTimeoutRef.current = {}
    },
  }))

  // Clear pending timeouts on unmount
  useEffect(() => {
    return () => {
      for (const timeout of Object.values(typingTimeoutRef.current)) {
        clearTimeout(timeout)
      }
    }
  }, [])

  if (typingUsers.length === 0) return null

  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  )
}))

export default function Messenger() {
  const [config, setConfig] = useState<WidgetConfig | null>(null)
  const [view, setView] = useState<ViewType>('detail')
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const newMessageRef = useRef('')
  const [hasText, setHasText] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [team, setTeam] = useState<Agent[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const composerTextareaRef = useRef<HTMLTextAreaElement>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [replyingToMessage, setReplyingToMessage] = useState<Message | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const typingIndicatorRef = useRef<TypingIndicatorHandle>(null)
  const [isStartingNewConversation, setIsStartingNewConversation] = useState(false)
  const typingNotifiedRef = useRef(false)
  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [currentPageUrl, setCurrentPageUrl] = useState<string | null>(null)
  const [availability, setAvailability] = useState<Availability | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false)
  const [ratingHover, setRatingHover] = useState<Record<number, number>>({})
  const [ratingSelected, setRatingSelected] = useState<Record<number, number>>({})
  const [ratingComment, setRatingComment] = useState<Record<number, string>>({})
  const [ratingShowComment, setRatingShowComment] = useState<Record<number, boolean>>({})
  const [isSubmittingRating, setIsSubmittingRating] = useState<Record<number, boolean>>({})
  // Force re-render every minute to update relative times
  const [timeTick, setTimeTick] = useState(0)
  const [screenshotSupported, setScreenshotSupported] = useState(false)
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false)
  // Deferred "mark as read" flag: set when an agent message arrives while the tab is hidden
  const pendingMarkReadRef = useRef(false)

  // Helpers to clear/restore uncontrolled textarea state via refs (avoids re-renders)
  function clearComposerText(): void {
    newMessageRef.current = ''
    setHasText(false)
    if (composerTextareaRef.current) composerTextareaRef.current.value = ''
  }

  function restoreComposerText(text: string): void {
    newMessageRef.current = text
    setHasText(true)
    if (composerTextareaRef.current) composerTextareaRef.current.value = text
  }

  const resizeComposer = useCallback(() => {
    const textarea = composerTextareaRef.current
    if (!textarea || typeof window === 'undefined') return

    textarea.style.height = 'auto'
    const computed = window.getComputedStyle(textarea)
    const lineHeight = Number.parseFloat(computed.lineHeight)
    const fontSize = Number.parseFloat(computed.fontSize)
    const resolvedLineHeight = Number.isFinite(lineHeight) ? lineHeight : fontSize * 1.4
    const paddingTop = Number.parseFloat(computed.paddingTop) || 0
    const paddingBottom = Number.parseFloat(computed.paddingBottom) || 0
    const borderTop = Number.parseFloat(computed.borderTopWidth) || 0
    const borderBottom = Number.parseFloat(computed.borderBottomWidth) || 0
    const maxHeight = resolvedLineHeight * MAX_COMPOSER_ROWS + paddingTop + paddingBottom + borderTop + borderBottom
    const contentHeight = textarea.scrollHeight + borderTop + borderBottom
    const nextHeight = Math.min(contentHeight, maxHeight)

    textarea.style.height = `${nextHeight}px`
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden'
  }, [])

  // Apply pre-filled text after state settles (triggered by view/loading changes)
  useEffect(() => {
    if (pendingPrefilledTextRef.current === null) return
    if (isLoading) return
    const text = pendingPrefilledTextRef.current
    pendingPrefilledTextRef.current = null
    restoreComposerText(text)
    composerTextareaRef.current?.focus()
    resizeComposer()
  }, [isLoading, isStartingNewConversation, currentConversation, resizeComposer])

  // Focus textarea when replying to a message
  useEffect(() => {
    if (replyingToMessage) {
      composerTextareaRef.current?.focus()
    }
  }, [replyingToMessage])

  // Update relative times every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeTick((t) => t + 1)
    }, 60000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    let supported = !!navigator.mediaDevices?.getDisplayMedia
    if (!supported) {
      try {
        supported = !!window.parent?.navigator?.mediaDevices?.getDisplayMedia
      } catch {
        // Cross-origin parent
      }
    }
    setScreenshotSupported(supported)
  }, [])

  // Resize composer when pendingFiles or replyingToMessage change (not on every keystroke)
  useEffect(() => {
    resizeComposer()
  }, [pendingFiles.length, resizeComposer])

  // Delegate typing events to the isolated TypingIndicator component
  const handleTypingReceived = useCallback((typingData: TypingData) => {
    typingIndicatorRef.current?.onTypingReceived(typingData)
  }, [])

  const apiRequest = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    if (!config) return null

    const url = `${config.apiUrl}/api/v1/widget${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Widget-App-Id': config.appId,
      'X-Widget-Session-Token': config.sessionToken,
      ...options.headers,
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }

    return response.json()
  }, [config])

  // Mark agent messages as read when the user is viewing the conversation
  const markMessagesRead = useCallback((conversationId: number) => {
    apiRequest('/mark_read', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId }),
    }).catch(error => console.error('Failed to mark messages as read:', error))
  }, [apiRequest])

  // Handle new message from WebSocket
  const handleMessageReceived = useCallback((message: Message) => {
    setMessages(prev => {
      // Avoid duplicates
      if (prev.some(m => m.id === message.id)) return prev
      return [...prev, message]
    })

    // Handle agent messages
    if (message.author_type === 'Agent') {
      const agent = message.author as Agent
      const agentName = agent?.name || 'Suporte'

      if (agent?.id) {
        // Update header to show the assigned agent when their first message arrives.
        // The backend auto-assigns on reply and broadcasts assignment_changed,
        // but as a resilience fallback we also derive it from the message itself.
        setCurrentConversation(prev =>
          prev && !prev.assigned_agent ? { ...prev, assigned_agent: agent } : prev
        )

      }

      // Clear typing indicators and record agent message time (delegated to child component)
      typingIndicatorRef.current?.onAgentMessageReceived(agent?.id ?? 0)

      // Mark as read only if the user is actually viewing the page
      if (currentConversation?.id) {
        if (document.visibilityState === 'visible') {
          markMessagesRead(currentConversation.id)
        } else {
          pendingMarkReadRef.current = true
        }
      }

      // Notify parent about unread
      window.parent.postMessage({ type: 'signal:messenger:notificationSound' }, '*')
      // Send message data for preview notification
      window.parent.postMessage({
        type: 'signal:messenger:newAgentMessage',
        message: {
          body: message.body,
          author_name: agentName,
          author_avatar: agent?.avatar_url || null,
          conversation_id: currentConversation?.id
        }
      }, '*')
    }
  }, [currentConversation?.id, markMessagesRead])

  // When the user returns to this tab, flush any deferred mark-as-read
  useEffect(() => {
    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible' && pendingMarkReadRef.current && currentConversation?.id) {
        pendingMarkReadRef.current = false
        markMessagesRead(currentConversation.id)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [currentConversation?.id, markMessagesRead])

  // Handler for global new message notifications (when not viewing that conversation)
  const handleGlobalNewMessage = useCallback((message: NewMessageNotification) => {
    // Ignore non-chat messages (e.g. WhatsApp, email) — widget only handles chat
    if (message.channel && message.channel !== 'chat') return

    // Only notify if this message is from a different conversation than the one we're viewing
    if (message.conversation_id !== currentConversation?.id) {
      const authorName = message.author_name || 'Suporte'
      window.parent.postMessage({
        type: 'signal:messenger:newAgentMessage',
        message: {
          body: message.body,
          author_name: authorName,
          author_avatar: message.author_avatar_url,
          conversation_id: message.conversation_id
        }
      }, '*')
      window.parent.postMessage({ type: 'signal:messenger:notificationSound' }, '*')
    }
  }, [currentConversation?.id])

  // Handler for message deleted notifications
  const handleMessageDeleted = useCallback((data: MessageDeletedNotification) => {
    // Update message in current conversation if it matches
    if (data.conversation_id === currentConversation?.id) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.message_id
            ? { ...msg, deleted_at: new Date().toISOString() }
            : msg
        )
      )
    }
  }, [currentConversation?.id])

  const handleMessageUpdated = useCallback((data: MessageUpdatedData) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === data.id
          ? {
              ...msg,
              body: data.body ?? msg.body,
              system_message_type: data.system_message_type ?? msg.system_message_type,
              metadata: (data.metadata as Message['metadata']) ?? msg.metadata,
              edited_at: data.edited_at ?? msg.edited_at,
            }
          : msg
      )
    )
  }, [])

  // Handle assignment change from WebSocket (updates header to show assigned agent)
  const handleAssignmentChanged = useCallback((data: { assigned_agent: Agent | null }) => {
    setCurrentConversation(prev => prev ? { ...prev, assigned_agent: data.assigned_agent } : prev)
  }, [])

  // WebSocket connection for current conversation
  const { notifyTyping } = useWidgetChannel({
    conversationId: currentConversation?.id || null,
    apiUrl: config?.apiUrl || '',
    sessionToken: config?.sessionToken || '',
    onMessageReceived: handleMessageReceived,
    onMessageUpdated: handleMessageUpdated,
    onTypingReceived: handleTypingReceived,
    onAssignmentChanged: handleAssignmentChanged,
  })

  // WebSocket connection for global notifications (all conversations)
  useEndUserChannel({
    apiUrl: config?.apiUrl || '',
    sessionToken: config?.sessionToken || '',
    onNewMessage: handleGlobalNewMessage,
    onMessageDeleted: handleMessageDeleted,
  })

  // Scroll to bottom when messages change or conversation changes
  useEffect(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    })
  }, [messages, currentConversation?.id])

  // Auto-scroll when content height changes (e.g., images loading)
  useEffect(() => {
    if (!currentConversation?.id || !messagesContainerRef.current) return

    let timeoutId: number | null = null
    let rafId: number | null = null
    const stopObservingAfter = 3000

    const observer = new ResizeObserver(() => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = null
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
      })
    })

    observer.observe(messagesContainerRef.current)

    timeoutId = window.setTimeout(() => {
      observer.disconnect()
    }, stopObservingAfter)

    return () => {
      observer.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [currentConversation?.id])

  // Track if data has been loaded to avoid re-fetching
  const hasLoadedRef = useRef(false)
  // Store pending conversation ID to load after init
  const pendingConversationIdRef = useRef<number | null>(null)
  // Text to pre-fill in the composer after state settles
  const pendingPrefilledTextRef = useRef<string | null>(null)
  // Stable refs for postMessage handler to avoid effect re-runs
  const configRef = useRef(config)
  configRef.current = config
  const apiRequestRef = useRef(apiRequest)
  apiRequestRef.current = apiRequest
  const loadSpecificConversationRef = useRef<((id: number) => Promise<void>) | null>(null)

  const loadConversation = useCallback(async () => {
    if (!config) return

    setIsLoading(true)
    try {
      const data = await apiRequest('/conversation')
      if (data) {
        setCurrentConversation(data.conversation)
        setMessages(data.messages || [])
      }
    } catch (error) {
      console.error('Failed to load conversation:', error)
    } finally {
      setIsLoading(false)
    }
  }, [config, apiRequest])

  const loadConversations = useCallback(async () => {
    if (!config) return

    try {
      const data = await apiRequest('/conversations')
      if (data) {
        setConversations(data.conversations || [])
      }
    } catch (error) {
      console.error('Failed to load conversations:', error)
    }
  }, [config, apiRequest])

  const upsertConversationPreview = useCallback((conversation: Conversation, message: Message) => {
    const trimmedBody = message.body?.trim()
    const lastMessage = trimmedBody
      ? trimmedBody.length > 50
        ? `${trimmedBody.slice(0, 47)}...`
        : trimmedBody
      : null
    const authorName = message.author?.name ? message.author.name.split(' ')[0] : null
    const lastMessageAt = conversation.last_message_at || message.created_at

    setConversations(prev => {
      const updated: Conversation = {
        id: conversation.id,
        status: conversation.status,
        closed_at: conversation.closed_at ?? null,
        assigned_agent: conversation.assigned_agent ?? null,
        last_message: lastMessage,
        last_message_author_name: authorName,
        last_message_has_attachments: (message.attachments?.length ?? 0) > 0,
        last_message_at: lastMessageAt,
        unread_count: 0,
        reply_deadline_at: conversation.reply_deadline_at ?? null,
        reply_allowed: conversation.reply_allowed,
      }

      const remaining = prev.filter(existing => existing.id !== conversation.id)
      return [updated, ...remaining]
    })
  }, [])

  const loadTeam = useCallback(async () => {
    if (!config) return

    try {
      const data = await apiRequest('/team')
      if (data) {
        setTeam(data.agents || [])
      }
    } catch (error) {
      console.error('Failed to load team:', error)
    }
  }, [config, apiRequest])

  const loadAvailability = useCallback(async () => {
    if (!config) return

    try {
      const data = await apiRequest('/availability')
      if (data) {
        setAvailability({
          available: data.available,
          message: data.message,
          next_open_at: data.next_open_at ?? null,
          next_open_time: data.next_open_time ?? null,
          organization_name: data.organization_name ?? null,
          exception_name: data.exception_name ?? null,
          exception_message: data.exception_message ?? null,
          in_exception: data.in_exception ?? false,
        })
      }
    } catch (error) {
      console.error('Failed to load availability:', error)
    }
  }, [config, apiRequest])

  // Auto-refresh availability when status should change
  useEffect(() => {
    if (!availability || !config) return

    // When available, poll infrequently to detect business hours ending
    if (availability.available) {
      const interval = setInterval(() => loadAvailability(), 300_000)
      return () => clearInterval(interval)
    }

    // When unavailable and next_open_at is in the past, refresh immediately
    const msUntilOpen = availability.next_open_at
      ? new Date(availability.next_open_at).getTime() - Date.now()
      : null

    if (msUntilOpen !== null && msUntilOpen <= 0) {
      loadAvailability()
      return
    }

    // Schedule a refresh when business hours should start (+ 2s buffer)
    const timeout = msUntilOpen !== null
      ? setTimeout(() => loadAvailability(), msUntilOpen + 2000)
      : null

    // Fallback: poll every 60s while unavailable in case next_open_at is missing or drifts
    const interval = setInterval(() => loadAvailability(), 60_000)

    return () => {
      if (timeout) clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [availability?.available, availability?.next_open_at, config, loadAvailability])

  const loadSpecificConversation = useCallback(async (conversationId: number) => {
    if (!config) return

    setIsLoading(true)
    try {
      const data = await apiRequest(`/conversation?conversation_id=${conversationId}`)
      if (data) {
        setCurrentConversation(data.conversation)
        setMessages(data.messages || [])
        setView('detail')
      }
    } catch (error) {
      console.error('Failed to load conversation:', error)
    } finally {
      setIsLoading(false)
    }
  }, [config, apiRequest])
  loadSpecificConversationRef.current = loadSpecificConversation

  // Handle postMessage from parent (mount-only — uses refs to avoid re-running)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data

      switch (type) {
        case 'signal:messenger:init':
          setConfig(event.data.config)
          if (event.data.config?.currentPageUrl) {
            setCurrentPageUrl(event.data.config.currentPageUrl)
          }
          break
        case 'signal:messenger:updatePageUrl':
          if (event.data.pageUrl) {
            setCurrentPageUrl(event.data.pageUrl)
          }
          break
        case 'signal:messenger:identify':
          // Update session token and reload all data after identity change
          if (event.data.sessionToken) {
            setConfig(prev => prev ? { ...prev, sessionToken: event.data.sessionToken } : prev)
          }
          // Preserve active conversation so it can be restored after reload,
          // then clear it. Using the setter callback avoids stale closure.
          setCurrentConversation(prev => {
            if (prev) {
              pendingConversationIdRef.current = prev.id
            }
            return null
          })
          setMessages([])
          setConversations([])
          setView('list')
          hasLoadedRef.current = false
          break
        case 'signal:messenger:open':
          // Clear unread when opened
          break
        case 'signal:messenger:openConversation':
          // Open a specific conversation
          if (event.data.conversationId) {
            pendingConversationIdRef.current = event.data.conversationId
            if (configRef.current) {
              loadSpecificConversationRef.current?.(event.data.conversationId)
            }
          }
          break
        case 'signal:messenger:startNewChat':
          // Always start a new conversation with the pre-filled text
          if (event.data.text) {
            pendingPrefilledTextRef.current = event.data.text
            setCurrentConversation(null)
            setMessages([])
            setReplyingToMessage(null)
            setIsStartingNewConversation(true)
            setView('detail')
          }
          break
        case 'signal:messenger:widgetClosed':
          // Reload conversation skipping closed ones so reopening shows a fresh state
          if (configRef.current) {
            apiRequestRef.current('/conversation?skip_closed=1')
              .then((data: { conversation: Conversation | null, messages: Message[] } | null) => {
                if (data) {
                  setCurrentConversation(data.conversation)
                  setMessages(data.messages || [])
                  if (!data.conversation) setView('detail')
                }
              })
              .catch((error: unknown) => console.error('Failed to reload conversation:', error))
          }
          break
      }
    }

    window.addEventListener('message', handleMessage)

    // Request init from parent exactly once on mount
    window.parent.postMessage({ type: 'signal:messenger:requestInit' }, '*')

    return () => window.removeEventListener('message', handleMessage)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load data when config is ready (only once)
  useEffect(() => {
    if (config && !hasLoadedRef.current) {
      hasLoadedRef.current = true

      // Check if we have a pending conversation to load
      if (pendingConversationIdRef.current) {
        loadSpecificConversation(pendingConversationIdRef.current)
        pendingConversationIdRef.current = null
      }

      // Load all data and then notify parent we're ready
      Promise.all([
        loadConversation(),
        loadTeam(),
        loadConversations(),
        loadAvailability()
      ]).finally(() => {
        // Signal parent that messenger is ready to be shown
        window.parent.postMessage({ type: 'signal:messenger:ready' }, '*')
      })
    }
  }, [config, loadConversation, loadTeam, loadConversations, loadAvailability, loadSpecificConversation])

  const hasMultipleDays = useMemo(() => conversationHasMultipleDays(messages), [messages])

  const hasMultipleAgents = useMemo(() => {
    const agentIds = new Set(
      messages
        .filter(m => m.author_type === 'Agent' && m.author?.id)
        .map(m => m.author.id)
    )
    return agentIds.size > 1
  }, [messages])

  // Track the first message ID of each agent (to show "joined" indicator)
  const agentFirstMessageIds = useMemo(() => {
    const seen = new Set<number>()
    const firstIds = new Set<number>()
    for (const m of messages) {
      if (m.author_type === 'Agent' && m.author?.id && !seen.has(m.author.id)) {
        seen.add(m.author.id)
        // Skip the very first agent — only show "joined" for subsequent agents
        if (seen.size > 1) {
          firstIds.add(m.id)
        }
      }
    }
    return firstIds
  }, [messages])

  const replyAllowed = useMemo((): boolean => {
    if (!currentConversation) return true
    if (currentConversation.status !== 'closed') return true
    if (currentConversation.reply_allowed !== undefined) return currentConversation.reply_allowed

    const deadline = currentConversation.reply_deadline_at
    if (deadline) {
      return Date.now() <= new Date(deadline).getTime()
    }

    const closedAt = currentConversation.closed_at
    if (closedAt) {
      const oneDayMs = 24 * 60 * 60 * 1000
      return Date.now() <= new Date(closedAt).getTime() + oneDayMs
    }

    return true
  }, [currentConversation, timeTick])

  const sendMessage = useCallback(async () => {
    const messageText = newMessageRef.current
    if (!config || (!messageText.trim() && pendingFiles.length === 0)) return
    if (!replyAllowed) return

    // Store the message content before clearing
    const messageToSend = messageText
    const filesToSend = [...pendingFiles]
    const wasStartingNew = isStartingNewConversation

    // Capture reply state before clearing
    const replyToId = replyingToMessage?.id

    // Clear input immediately for better UX
    clearComposerText()
    resizeComposer()
    setPendingFiles([])
    setReplyingToMessage(null)
    setIsSending(true)

    try {
      const formData = new FormData()
      formData.append('body', messageToSend)

      if (replyToId) {
        formData.append('replied_to_message_id', replyToId.toString())
      }

      // Include sender context (page URL where message was sent from)
      if (currentPageUrl) {
        formData.append('page_url', currentPageUrl)
      }

      // Signal backend to create a new conversation if user explicitly started one
      if (wasStartingNew) {
        formData.append('new_conversation', 'true')
      }

      // Send conversation ID if we have one (allows reopening closed/snoozed conversations)
      if (currentConversation?.id) {
        formData.append('conversation_id', currentConversation.id.toString())
      }

      filesToSend.forEach(file => {
        formData.append('attachments[]', file)
      })

      const response = await fetch(`${config.apiUrl}/api/v1/widget/messages`, {
        method: 'POST',
        headers: {
          'X-Widget-App-Id': config.appId,
          'X-Widget-Session-Token': config.sessionToken,
        },
        body: formData,
      })

      if (response.ok) {
        const data = await response.json()
        // Check if this is a new conversation (either explicitly started or first message)
        const isNewConversation = !currentConversation && data.conversation
        // Update current conversation with the one returned from backend
        if (data.conversation) {
          setCurrentConversation(data.conversation)
        }
        // When starting a new conversation (or sending first message without existing conversation),
        // add the message directly from API response because WebSocket wasn't connected yet
        if ((wasStartingNew || isNewConversation) && data.message) {
          setMessages([data.message])
        }
        setIsStartingNewConversation(false)
        if (data.conversation && data.message) {
          upsertConversationPreview(data.conversation, data.message)
        }

        // System messages arrive via WebSocket, no need to add them here
      } else {
        if (response.status === 403) {
          setCurrentConversation(prev => prev ? { ...prev, reply_allowed: false } : prev)
        }
        // If request failed, restore the message
        restoreComposerText(messageToSend)
        setPendingFiles(filesToSend)
        console.error('Failed to send message: server returned', response.status)
      }
    } catch (error) {
      // If request failed, restore the message
      restoreComposerText(messageToSend)
      setPendingFiles(filesToSend)
      console.error('Failed to send message:', error)
    } finally {
      setIsSending(false)
    }
  }, [config, pendingFiles, isStartingNewConversation, currentConversation, upsertConversationPreview, currentPageUrl, replyAllowed, resizeComposer, replyingToMessage])

  const submitEmail = useCallback(async () => {
    if (!config || !emailInput.trim() || !emailInput.includes('@')) return

    setIsSubmittingEmail(true)
    try {
      const response = await fetch(`${config.apiUrl}/api/v1/widget/update_email`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-App-Id': config.appId,
          'X-Widget-Session-Token': config.sessionToken,
        },
        body: JSON.stringify({
          email: emailInput.trim()
        })
      })

      if (response.ok) {
        const submittedEmail = emailInput.trim()
        // Update all email_capture messages to show confirmed state
        setMessages(prev => prev.map(msg => {
          if (msg.system_message_type === 'email_capture') {
            return {
              ...msg,
              body: 'Você será notificado aqui e por email',
              metadata: { email_captured: true, email: submittedEmail }
            }
          }
          return msg
        }))
        setEmailInput('')
      }
    } catch (error) {
      console.error('Failed to update email:', error)
    } finally {
      setIsSubmittingEmail(false)
    }
  }, [config, emailInput])

  const submitRating = useCallback(async (messageId: number) => {
    if (!config || !currentConversation || !ratingSelected[messageId]) return

    setIsSubmittingRating(prev => ({ ...prev, [messageId]: true }))
    try {
      const response = await fetch(`${config.apiUrl}/api/v1/widget/agent_rating`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-App-Id': config.appId,
          'X-Widget-Session-Token': config.sessionToken,
        },
        body: JSON.stringify({
          conversation_id: currentConversation.id,
          message_id: messageId,
          rating: ratingSelected[messageId],
          comment: ratingComment[messageId] || null
        })
      })

      if (response.ok) {
        setMessages(prev => prev.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                rated: true,
                rating: ratingSelected[messageId],
                has_comment: !!ratingComment[messageId]
              }
            }
          }
          return msg
        }))
      }
    } catch (error) {
      console.error('Failed to submit rating:', error)
    } finally {
      setIsSubmittingRating(prev => ({ ...prev, [messageId]: false }))
    }
  }, [config, currentConversation, ratingSelected, ratingComment])

  const handleClose = () => {
    window.parent.postMessage({ type: 'signal:messenger:close' }, '*')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setPendingFiles(prev => [...prev, ...files])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const currentTarget = e.currentTarget as HTMLElement
    if (!currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files])
    }
  }, [])

  const handleScreenshot = async () => {
    // Try iframe's own mediaDevices, then parent's (same-origin fallback)
    let mediaDevices = navigator.mediaDevices
    if (!mediaDevices) {
      try {
        mediaDevices = window.parent?.navigator?.mediaDevices
      } catch {
        // Cross-origin — parent access blocked
      }
    }
    if (!mediaDevices?.getDisplayMedia) return

    setIsCapturingScreenshot(true)
    let stream: MediaStream | null = null
    try {
      stream = await mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' } as MediaTrackConstraints,
        preferCurrentTab: true,
        audio: false,
      } as DisplayMediaStreamOptions)

      const video = document.createElement('video')
      video.srcObject = stream
      video.muted = true
      video.playsInline = true
      await new Promise<void>(resolve => { video.onloadedmetadata = () => resolve() })
      await video.play()

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx?.drawImage(video, 0, 0)

      stream.getTracks().forEach(t => t.stop())
      stream = null

      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'))
      if (blob && config) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const file = new File([blob], `screenshot-${timestamp}.png`, { type: 'image/png' })

        const formData = new FormData()
        formData.append('body', '')
        formData.append('attachments[]', file)
        if (currentPageUrl) formData.append('page_url', currentPageUrl)
        if (currentConversation?.id) formData.append('conversation_id', currentConversation.id.toString())
        if (isStartingNewConversation) formData.append('new_conversation', 'true')

        const response = await fetch(`${config.apiUrl}/api/v1/widget/messages`, {
          method: 'POST',
          headers: {
            'X-Widget-App-Id': config.appId,
            'X-Widget-Session-Token': config.sessionToken,
          },
          body: formData,
        })

        if (response.ok) {
          const data = await response.json()
          if (data.conversation) setCurrentConversation(data.conversation)
          if ((isStartingNewConversation || !currentConversation) && data.message) {
            setMessages(prev => [...prev, data.message])
          }
          if (data.conversation && data.message) {
            upsertConversationPreview(data.conversation, data.message)
          }
          setIsStartingNewConversation(false)
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        // User cancelled the picker
      } else {
        console.error('Screenshot capture failed:', err)
      }
    } finally {
      if (stream) {
        stream.getTracks().forEach(t => t.stop())
      }
      setIsCapturingScreenshot(false)
    }
  }

  const removeFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
  }

  const openImage = (url: string) => {
    window.parent.postMessage({ type: 'signal:messenger:openImage', url }, '*')
  }

  const openVideo = (url: string) => {
    window.parent.postMessage({ type: 'signal:messenger:openVideo', url }, '*')
  }

  const isVideo = (attachment: Attachment): boolean => {
    if (attachment.is_video !== undefined) return attachment.is_video
    return attachment.content_type.startsWith('video/')
  }

  const isImage = (attachment: Attachment): boolean => {
    if (attachment.is_image !== undefined) return attachment.is_image
    return attachment.content_type.startsWith('image/')
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const notifyTypingDebounced = useCallback((hasText: boolean) => {
    if (hasText) {
      if (!typingNotifiedRef.current) {
        typingNotifiedRef.current = true
        notifyTyping(true)
      }
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current)
      typingDebounceRef.current = setTimeout(() => {
        typingNotifiedRef.current = false
        notifyTyping(false)
      }, 2000)
    } else {
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current)
      if (typingNotifiedRef.current) {
        typingNotifiedRef.current = false
        notifyTyping(false)
      }
    }
  }, [notifyTyping])

  useEffect(() => {
    if (!replyAllowed) {
      clearComposerText()
      setPendingFiles([])
      notifyTypingDebounced(false)
    }
  }, [replyAllowed, notifyTypingDebounced])

  if (!config) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <header
        className="flex items-center justify-between px-4 py-3 text-white"
        style={{ backgroundColor: config.brandColor }}
      >
        <div className="flex items-center gap-3">
          {view === 'detail' && conversations.length > 0 && (
            <button
              onClick={() => { setView('list'); setReplyingToMessage(null) }}
              className="p-1 hover:bg-white/20 rounded transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}

          {/* Team avatars or assigned agent */}
          <div className="flex -space-x-2">
            {view === 'detail' && currentConversation?.assigned_agent ? (
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium">
                {currentConversation.assigned_agent.avatar_url ? (
                  <img
                    src={currentConversation.assigned_agent.avatar_url}
                    alt={currentConversation.assigned_agent.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  (currentConversation.assigned_agent.name || 'S').charAt(0).toUpperCase()
                )}
              </div>
            ) : (
              team.slice(0, 3).map((agent) => (
                <div
                  key={agent.id}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium border-2 border-current"
                  style={{ borderColor: config.brandColor }}
                >
                  {agent.avatar_url ? (
                    <img src={agent.avatar_url} alt={agent.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    (agent.name || 'S').charAt(0).toUpperCase()
                  )}
                </div>
              ))
            )}
          </div>

          <div>
            <h1 className="font-semibold text-sm">
              {(view === 'detail' && currentConversation?.assigned_agent?.name) || 'Suporte'}
            </h1>
            <p className="text-xs opacity-80">
              {availability && !availability.available && availability.message ? (
                <>
                  <Clock className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                  {availability.message}
                </>
              ) : (
                'Normalmente respondemos em poucos minutos'
              )}
            </p>
          </div>
        </div>

        {!config.embedded && (
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white/20 rounded transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </header>

      {/* Conversation List View */}
      {view === 'list' ? (
        <div className="flex-1 overflow-y-auto">
          {/* New conversation button */}
          <button
            onClick={() => {
              setCurrentConversation(null)
              setMessages([])
              setReplyingToMessage(null)
              setIsStartingNewConversation(true)
              setView('detail')
            }}
            className="w-full p-4 border-b border-gray-100 flex items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: config.brandColor }}
            >
              <Plus className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium text-gray-900">Iniciar nova conversa</span>
          </button>

          {/* Conversation list */}
          {conversations.map(conv => {
            const agentName = conv.assigned_agent?.name || 'Assistente'
            const authorPrefix = conv.last_message_author_name ? `${conv.last_message_author_name}: ` : ''
            const strippedBody = conv.last_message ? stripHtml(conv.last_message).trim() : ''
            const previewText = strippedBody
              ? `${authorPrefix}${strippedBody}`
              : conv.last_message_has_attachments
                ? `${authorPrefix}Enviou um anexo`
                : 'Nenhuma mensagem ainda'
            return (
              <button
                key={conv.id}
                onClick={() => {
                  setIsStartingNewConversation(false)
                  loadSpecificConversation(conv.id)
                }}
                className={`w-full p-4 border-b border-gray-100 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left cursor-pointer ${
                  conv.id === currentConversation?.id ? 'bg-gray-50' : ''
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  {conv.assigned_agent?.avatar_url ? (
                    <img
                      src={conv.assigned_agent.avatar_url}
                      alt={agentName}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <MessageSquare className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 truncate">
                      Chat com {agentName}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(conv.last_message_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate mt-0.5">
                    {previewText}
                  </p>
                </div>
                {conv.unread_count > 0 && (
                  <span className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center flex-shrink-0">
                    {conv.unread_count}
                  </span>
                )}
              </button>
            )
          })}

          {conversations.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nenhuma conversa anterior</p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4">
            <div ref={messagesContainerRef} className="space-y-4">
            {/* Exception message info card */}
            {availability && !availability.available && availability.exception_message && (
              <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-100 rounded-xl mb-2">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800 whitespace-pre-wrap">
                  {availability.exception_message}
                </p>
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <p className="text-lg font-medium mb-2">{config.welcomeText}</p>
                <p className="text-sm">Envie uma mensagem e responderemos em breve.</p>
              </div>
            ) : (
          messages.map((message, index) => {
            const prevMessage = messages[index - 1]
            const nextMessage = messages[index + 1]
            const showDateSeparator = shouldShowDateSeparator(
              message.created_at,
              prevMessage?.created_at ?? null,
              index === 0,
              hasMultipleDays
            )
            // Only show time on the last message of an agent group
            const isLastOfAgentGroup = message.author_type === 'Agent' &&
              (!nextMessage || nextMessage.author_type !== 'Agent' || nextMessage.author.id !== message.author.id)

            // System message: away_notice (centered gray message)
            if (message.system_message_type === 'away_notice') {
              return (
                <div key={message.id}>
                  {showDateSeparator && (
                    <div className="flex items-center justify-center my-3">
                      <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-start my-2">
                    <div className="bg-gray-100 rounded-2xl px-4 py-2 text-sm text-gray-600 rounded-bl-md">
                      {message.body}
                    </div>
                  </div>
                </div>
              )
            }

            // System message: email_capture (interactive card)
            if (message.system_message_type === 'email_capture') {
              const emailCaptured = message.metadata?.email_captured
              return (
                <div key={message.id}>
                  {showDateSeparator && (
                    <div className="flex items-center justify-center my-3">
                      <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-start my-2">
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 max-w-[90%] rounded-bl-md">
                      {emailCaptured ? (
                        <>
                          <p className="text-sm text-gray-700">Você será notificado aqui e por email</p>
                          <p className="text-sm text-gray-500 mt-1">{message.metadata?.email}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-700 mb-3">{message.body}</p>
                          <div className="flex gap-2">
                            <input
                              type="email"
                              placeholder="email@exemplo.com"
                              value={emailInput}
                              onChange={(e) => setEmailInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  submitEmail()
                                }
                              }}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-gray-300"
                            />
                            <button
                              onClick={submitEmail}
                              disabled={!emailInput.includes('@') || isSubmittingEmail}
                              className="px-3 py-2 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                              style={{ backgroundColor: config.brandColor }}
                            >
                              {isSubmittingEmail ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            }

            // System message: rating_request (interactive star rating card)
            if (message.system_message_type === 'rating_request') {
              const isRated = message.metadata?.rated
              const currentHover = ratingHover[message.id] || 0
              const currentSelected = ratingSelected[message.id] || 0
              const displayRating = isRated ? (message.metadata?.rating || 0) : (currentHover || currentSelected)

              return (
                <div key={message.id}>
                  {showDateSeparator && (
                    <div className="flex items-center justify-center my-3">
                      <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-start my-2">
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 max-w-[90%] rounded-bl-md">
                      {isRated ? (
                        <div className="text-center">
                          <div className="flex justify-center gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <span key={star} className="text-lg">
                                {star <= (message.metadata?.rating || 0) ? '★' : '☆'}
                              </span>
                            ))}
                          </div>
                          <p className="text-sm text-gray-500">Avaliação enviada. Obrigado!</p>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-gray-700 mb-3">{message.body}</p>
                          <div className="flex justify-center gap-2 mb-2">
                            {[1, 2, 3, 4, 5].map(star => (
                              <button
                                key={star}
                                onMouseEnter={() => setRatingHover(prev => ({ ...prev, [message.id]: star }))}
                                onMouseLeave={() => setRatingHover(prev => ({ ...prev, [message.id]: 0 }))}
                                onClick={() => setRatingSelected(prev => ({ ...prev, [message.id]: star }))}
                                className="text-2xl cursor-pointer transition-transform hover:scale-110"
                                style={{ color: star <= displayRating ? config.brandColor : '#d1d5db' }}
                              >
                                {star <= displayRating ? '★' : '☆'}
                              </button>
                            ))}
                          </div>
                          {currentSelected > 0 && (
                            <>
                              {!ratingShowComment[message.id] ? (
                                <button
                                  onClick={() => setRatingShowComment(prev => ({ ...prev, [message.id]: true }))}
                                  className="text-xs text-gray-600 hover:text-gray-900 underline cursor-pointer mb-3 mt-1 w-full text-center"
                                >
                                  Adicionar comentário (opcional)
                                </button>
                              ) : (
                                <textarea
                                  ref={el => el?.focus()}
                                  value={ratingComment[message.id] || ''}
                                  onChange={e => setRatingComment(prev => ({ ...prev, [message.id]: e.target.value }))}
                                  placeholder="Deixe um comentário..."
                                  maxLength={1000}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-gray-300 resize-none mb-2"
                                  rows={2}
                                />
                              )}
                              <button
                                onClick={() => submitRating(message.id)}
                                disabled={isSubmittingRating[message.id]}
                                className="w-full px-3 py-2 text-white rounded-xl text-sm font-medium disabled:opacity-50 cursor-pointer"
                                style={{ backgroundColor: config.brandColor }}
                              >
                                {isSubmittingRating[message.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                                ) : (
                                  'Enviar avaliação'
                                )}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            }

            // Outbound post: render as styled card instead of regular message bubble
            if (message.metadata?.outbound_post) {
              const postMeta = message.metadata
              const isLargePost = postMeta.post_format === 'large'
              return (
                <div key={message.id}>
                  {showDateSeparator && (
                    <div className="flex items-center justify-center my-3">
                      <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-start my-2">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 max-w-[90%]">
                      {/* Author */}
                      <div className="flex items-center gap-2 mb-3">
                        {message.author_type === 'Agent' && 'avatar_url' in message.author && message.author.avatar_url ? (
                          <img
                            src={message.author.avatar_url}
                            alt={message.author.name}
                            className="w-9 h-9 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-medium">
                            {message.author.name.charAt(0)}
                          </div>
                        )}
                        <span className="text-sm text-gray-500">{message.author.name}</span>
                      </div>
                      {/* Title */}
                      {postMeta.title && (
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{postMeta.title}</h3>
                      )}
                      {/* Body - truncated for large posts */}
                      {isLargePost ? (
                        <>
                          <div
                            className="text-sm text-gray-700 line-clamp-3 overflow-hidden"
                            dangerouslySetInnerHTML={{ __html: message.body }}
                          />
                          <button
                            onClick={() => {
                              window.parent.postMessage({
                                type: 'signal:widget:showLargePost',
                                postData: {
                                  title: postMeta.title || null,
                                  body: message.body,
                                  button_text: postMeta.button_text || null,
                                  button_url: postMeta.button_url || null,
                                  author_name: message.author.name,
                                  author_avatar_url: message.author_type === 'Agent' && 'avatar_url' in message.author ? message.author.avatar_url : null,
                                },
                              }, '*')
                            }}
                            className="text-sm font-medium mt-2 cursor-pointer hover:underline"
                            style={{ color: config.brandColor }}
                          >
                            Mostrar mais
                          </button>
                        </>
                      ) : (
                        <div
                          className="text-sm text-gray-700"
                          dangerouslySetInnerHTML={{ __html: message.body }}
                        />
                      )}
                      {/* CTA Button */}
                      {postMeta.button_text && postMeta.button_url && (
                        <div className="mt-3">
                          <a
                            href={postMeta.button_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-5 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                          >
                            {postMeta.button_text}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            }

            // Regular message
            const isDeleted = !!message.deleted_at
            return (
              <div key={message.id} id={`msg-${message.id}`}>
                {showDateSeparator && (
                  <div className="flex items-center justify-center my-3">
                    <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                      {formatDateSeparator(message.created_at)}
                    </span>
                  </div>
                )}
                {agentFirstMessageIds.has(message.id) && (
                  <div className="flex items-center justify-center gap-1.5 my-2">
                    {message.author_type === 'Agent' && 'avatar_url' in message.author && message.author.avatar_url ? (
                      <img
                        src={message.author.avatar_url}
                        alt={message.author.name}
                        className="w-4 h-4 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-gray-300 flex items-center justify-center text-white text-[8px] font-medium">
                        {message.author.name.charAt(0)}
                      </div>
                    )}
                    <span className="text-xs text-gray-400">
                      {message.author.name.split(' ')[0]} entrou na conversa
                    </span>
                  </div>
                )}
                <div
                  className={`group flex flex-col ${message.author_type === 'EndUser' ? 'items-end' : 'items-start'}`}
                >
                  <div className={`flex items-center gap-1 max-w-[80%] ${message.author_type === 'EndUser' ? 'flex-row-reverse' : ''}`}>
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        isDeleted
                          ? 'bg-gray-100 text-gray-400 italic rounded-bl-md'
                          : message.author_type === 'EndUser'
                            ? 'bg-blue-100 text-gray-900 rounded-br-md'
                            : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}
                    >
                    {isDeleted ? (
                      <span className="text-sm">Esta mensagem foi removida</span>
                    ) : (
                      <>
                        {message.replied_to && (
                          <QuotedMessage
                            repliedTo={message.replied_to}
                            onClick={() => {
                              const el = document.getElementById(`msg-${message.replied_to!.id}`)
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                el.classList.add('bg-yellow-50')
                                setTimeout(() => el.classList.remove('bg-yellow-50'), 1500)
                              }
                            }}
                            compact
                          />
                        )}
                        <MessageBody content={message.body} className="text-sm break-words" onImageClick={(images, index) => openImage(images[index].url)} />

                        {/* Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {message.attachments.map(att => (
                              <div key={att.id}>
                                {isImage(att) ? (
                                  <img
                                    src={att.url}
                                    alt={att.filename}
                                    className="max-w-full rounded cursor-pointer hover:opacity-90"
                                    onClick={() => openImage(att.url)}
                                    onLoad={() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })}
                                  />
                                ) : isVideo(att) ? (
                                  <div
                                    className="relative cursor-pointer group"
                                    onClick={() => openVideo(att.url)}
                                  >
                                    <video
                                      src={att.url}
                                      preload="metadata"
                                      muted
                                      playsInline
                                      className="w-full max-w-[200px] h-auto rounded object-cover"
                                      onLoadedMetadata={(e) => {
                                        // Seek to first frame to show thumbnail
                                        const video = e.target as HTMLVideoElement
                                        video.currentTime = 0.1
                                      }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors rounded">
                                      <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                                        <Play className="w-5 h-5 text-gray-800 ml-0.5" fill="currentColor" />
                                      </div>
                                    </div>
                                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[8px] font-bold">
                                      VIDEO
                                    </div>
                                    <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[8px]">
                                      {formatFileSize(att.byte_size)}
                                    </div>
                                  </div>
                                ) : (
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs underline"
                                  >
                                    {att.filename}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!isDeleted && (
                    <button
                      onClick={() => setReplyingToMessage(message)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-gray-600 rounded"
                      aria-label="Responder"
                    >
                      <Reply className="w-3.5 h-3.5" />
                    </button>
                  )}
                  </div>
                  {(isLastOfAgentGroup || (message.author_type === 'Agent' && message.edited_at)) && (
                    <p className="text-xs mt-1 text-gray-400">
                      {isLastOfAgentGroup && hasMultipleAgents && (
                        <>
                          <span className="font-medium">{message.author.name.split(' ')[0]}</span>
                          {' • '}
                        </>
                      )}
                      {isLastOfAgentGroup && formatRelativeTime(message.created_at)}
                      {message.author_type === 'Agent' && message.edited_at && (
                        <>
                          {isLastOfAgentGroup ? ' • ' : ''}
                          Editado
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )
          })
        )}

        {/* Typing indicator (isolated to avoid re-rendering messages) */}
        <TypingIndicator ref={typingIndicatorRef} />

        <div ref={messagesEndRef} />
            </div>
      </div>

      {/* Composer drop zone */}
      <div
        className="relative"
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-xl">
            <span className="text-sm font-medium text-blue-600">Soltar arquivos aqui</span>
          </div>
        )}

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 flex gap-2 flex-wrap">
          {pendingFiles.map((file, index) => (
            <div key={index} className="relative group">
              {file.type.startsWith('image/') && file.type !== 'image/heic' && file.type !== 'image/heif' ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="w-16 h-16 object-cover rounded"
                />
              ) : file.type.startsWith('video/') ? (
                <div className="w-16 h-16 bg-gray-800 rounded flex items-center justify-center relative">
                  <Play className="w-6 h-6 text-white" />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5 rounded-b">
                    VIDEO
                  </div>
                </div>
              ) : (
                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-500 p-1 text-center">
                  {file.name.slice(0, 10)}...
                </div>
              )}
              <button
                onClick={() => removeFile(index)}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Reply preview */}
      {replyingToMessage && replyAllowed && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50">
          <div className="flex-1 min-w-0">
            <QuotedMessage repliedTo={{
              id: replyingToMessage.id,
              body: replyingToMessage.body,
              author_type: replyingToMessage.author_type,
              author_name: replyingToMessage.author.name || 'Visitante',
              attachments_count: replyingToMessage.attachments?.length || 0,
              deleted: !!replyingToMessage.deleted_at,
              image_url: replyingToMessage.attachments?.find(a => a.content_type?.startsWith('image/'))?.url,
            }} compact />
          </div>
          <button
            onClick={() => setReplyingToMessage(null)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Composer */}
        {replyAllowed ? (
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-end gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                aria-label="Anexar arquivo"
              >
                <Paperclip className="w-5 h-5" />
              </button>

              {screenshotSupported && (
                <button
                  onClick={handleScreenshot}
                  disabled={isCapturingScreenshot}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Captura de tela"
                >
                  {isCapturingScreenshot ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <MonitorUp className="w-5 h-5" />
                  )}
                </button>
              )}

              <textarea
                ref={composerTextareaRef}
                defaultValue=""
                onChange={e => {
                  const value = e.target.value
                  newMessageRef.current = value
                  setHasText(value.trim().length > 0)
                  resizeComposer()
                  notifyTypingDebounced(value.length > 0)
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => notifyTypingDebounced(false)}
                placeholder="Digite uma mensagem..."
                className="flex-1 resize-none border border-gray-200 rounded-2xl px-4 py-2 text-sm focus:outline-none focus:border-gray-300"
                rows={1}
              />

              <button
                onClick={sendMessage}
                disabled={isSending || (!hasText && pendingFiles.length === 0)}
                className="p-2 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                style={{ backgroundColor: config.brandColor }}
                aria-label="Enviar mensagem"
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 border-t border-gray-100 text-center text-sm text-gray-500">
            Sua conversa terminou
          </div>
        )}
      </div>
        </>
      )}
    </div>
  )
}
